import React from "react";
import { AmenityIcon } from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { Lodging } from "@b2bportal/lodging-api/lib/api";
import { Amenity } from "@b2bportal/lodging-api/lib/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import defaultStyles from "./Tags.module.scss";

const MAX_AMOUNT_OF_TAGS = 3;

export type TagsProps = PlatformComponentProps & {
  hotel: Lodging;
  className?: string;
};

export const Tags = ({ hotel, className }: TagsProps) => {
  const styles = useHotelsStyles(
    CoreHotelsComponent.HotelCardTags,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.HotelCardTags);
  const { amenities } = hotel.lodging;
  const { t } = useI18nContext();

  const displayedAmenities = [
    {
      matchingKinds: [Amenity.FreeBreakfast],
      amenity: Amenity.FreeBreakfast,
      label: t(
        "core-hotels.availabilityPage.results.hotelCard.features.breakfastFree"
      ),
    },
    {
      matchingKinds: [Amenity.PaidBreakfast],
      amenity: Amenity.PaidBreakfast,
      label: t(
        "core-hotels.availabilityPage.results.hotelCard.features.breakfastAvailable"
      ),
    },
    {
      matchingKinds: [Amenity.RoomWifi, Amenity.FreeWifi],
      amenity: Amenity.FreeWifi,
      label: t("hotels.popularAmenities.wifi"),
    },
    {
      matchingKinds: [
        Amenity.FitnessCenter,
        Amenity.TwentyFourHourFitnessFacilities,
        Amenity.FitnessFacilities,
      ],
      amenity: Amenity.FitnessCenter,
      label: t("hotels.popularAmenities.gym"),
    },
    {
      matchingKinds: [
        Amenity.Parking,
        Amenity.FreeSelfParking,
        Amenity.PaidSelfParking,
        Amenity.PaidValetParking,
        Amenity.ParkingAvailableGeneral,
      ],
      amenity: Amenity.FreeSelfParking,
      label: t("hotels.popularAmenities.parking"),
    },
    {
      matchingKinds: [
        Amenity.Pool,
        Amenity.OutdoorPool,
        Amenity.IndoorPool,
        Amenity.SwimmingPool,
        Amenity.OutdoorSeasonalPool,
        Amenity.PoolGeneral,
      ],
      amenity: Amenity.Pool,
      label: t("hotels.popularAmenities.pool"),
    },
    {
      matchingKinds: [Amenity.Restaurant, Amenity.NumberOfRestaurants],
      amenity: Amenity.Restaurant,
      label: t("hotels.popularAmenities.restaurant"),
    },
    {
      matchingKinds: [Amenity.TwentyFourHourFrontDesk],
      amenity: Amenity.TwentyFourHourFrontDesk,
      label: t("hotels.popularAmenities.twentyFourHourFrontDesk"),
    },
  ];

  // Filter amenities that match the hotel's amenities
  const matchingAmenities = displayedAmenities
    .filter((amenity) =>
      amenity.matchingKinds.some((kind) =>
        amenities?.some((a) => a.amenity === kind)
      )
    )
    .slice(0, MAX_AMOUNT_OF_TAGS);

  if (matchingAmenities.length === 0) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      {matchingAmenities.map((amenityInfo, index) => (
        <div key={index} className={cn("tag")}>
          <AmenityIcon
            amenity={amenityInfo.amenity}
            className={cn("icon")}
            alt={amenityInfo.label}
          />
          <div className={cn("label")}>{amenityInfo.label}</div>
        </div>
      ))}
    </div>
  );
};
